import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useRights, useStore } from '../../../hooks';
import { ApplicantSearchContext } from 'types';
import { PhoneInput, toDigits } from 'components/ui';
import { LoaderWrapper, Wrapper } from './phone.styled';
import { LinesLoader } from '../../loaders';

interface IProps {
  inCallCard?: boolean;
  isDisabledSearch?: boolean;
}

export const Phone = observer(
  ({ inCallCard = false, isDisabledSearch = false }: IProps) => {
    const {
      createRequest,
      houses,
      meters,
      tenants,
      requests,
      areas,
      aiRequests,
    } = useStore();
    const tenantInfoTab = useRights('tenant_info');
    const lastAttemptedSocialPhone = useRef('');
    const callCardSocialPhone = useRef('');
    const callCardSocialRequestGeneration = useRef(0);

    const {
      loading,
      initialPhoneNumber,
      setPhoneNumber,
      getSocialNetworksByPhone,
      handlePhoneInput,
      socialNetworks,
    } = createRequest;

    const clearRelatedData = useCallback(() => {
      meters.clearAreaMeters();
      tenants.clearTenantStats();
      tenants.clearTenantSectors();
      requests.clearHistory();
      requests.clearCurrentSecondaryData();
      houses.clearHouseData();
      areas.clearAll();
    }, [areas, houses, meters, requests, tenants]);

    const searchSocialNetworks = useCallback<
      NonNullable<ApplicantSearchContext['searchSocialNetworks']>
    >(
      (phone, isCurrent) => {
        if (tenantInfoTab.update && isCurrent()) {
          lastAttemptedSocialPhone.current = phone;
          getSocialNetworksByPhone(phone, isCurrent);
        }
      },
      [getSocialNetworksByPhone, tenantInfoTab.update]
    );

    const loadHouse = useCallback<
      NonNullable<ApplicantSearchContext['loadHouse']>
    >(
      (houseId, isCurrent) => {
        if (isCurrent()) {
          houses.getHouseData(houseId, isCurrent);
        }
      },
      [houses]
    );

    const onTenantSelected = useCallback<
      NonNullable<ApplicantSearchContext['onTenantSelected']>
    >(
      async (memberId, areaId, isCurrent) => {
        if (!isCurrent()) {
          return;
        }

        await tenants.getTenantSectors(memberId, isCurrent);

        if (isCurrent()) {
          meters.getAreaMetersAllReadings({ area: areaId });
        }
      },
      [meters, tenants]
    );

    const applicantSearchContext = useMemo<ApplicantSearchContext>(
      () => ({
        clearRelatedData,
        loadHouse,
        onTenantSelected,
        searchSocialNetworks,
      }),
      [clearRelatedData, loadHouse, onTenantSelected, searchSocialNetworks]
    );

    const onPhoneInputChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const phone = toDigits(event.target.value);

        if (isDisabledSearch) {
          setPhoneNumber('initialPhoneNumber', phone);

          return;
        }

        handlePhoneInput(phone, applicantSearchContext);
      },
      [
        applicantSearchContext,
        handlePhoneInput,
        isDisabledSearch,
        setPhoneNumber,
      ]
    );

    useEffect(() => {
      if (callCardSocialPhone.current !== initialPhoneNumber) {
        callCardSocialPhone.current = initialPhoneNumber;
        callCardSocialRequestGeneration.current += 1;
      }

      if (
        tenantInfoTab.update &&
        /^\d{10}$/.test(initialPhoneNumber) &&
        socialNetworks?.phone_number !== initialPhoneNumber &&
        lastAttemptedSocialPhone.current !== initialPhoneNumber
      ) {
        lastAttemptedSocialPhone.current = initialPhoneNumber;
        const requestGeneration = callCardSocialRequestGeneration.current;
        const shouldApply = () =>
          requestGeneration === callCardSocialRequestGeneration.current;

        getSocialNetworksByPhone(initialPhoneNumber, shouldApply);
      }
    }, [
      getSocialNetworksByPhone,
      inCallCard,
      initialPhoneNumber,
      socialNetworks?.phone_number,
      tenantInfoTab.update,
    ]);

    return (
      <Wrapper>
        <PhoneInput
          style={{ width: '100%' }}
          onChange={onPhoneInputChange}
          disabled={inCallCard || aiRequests.isAiMode}
          value={initialPhoneNumber}
        />

        {loading.socials.pending && (
          <LoaderWrapper>
            <LinesLoader size="small" />
          </LoaderWrapper>
        )}
      </Wrapper>
    );
  }
);
